<template>
  <b-overlay :show="loading">

    <div class="board-shell">
      <CsrStrip v-if="csr" :csr="csr" />
    
    <div ref="wrap" class="board-wrap" :class="{ 'has-csr': !!csr }" :style="wrapStyle">

      <img v-if="bgUrl" :src="bgSrc" class="board-bg" alt="" @load="onResize" />

      <!-- Team Pace HUD -->
      <div
        v-if="teamPaceText || teamPaceImageUrl"
        class="hud"
        :style="{ left: hudMargin + 'px', top: hudMargin + 'px', width: (wrapWidth - hudMargin * 2) + 'px' }"
      >
        <img
          v-if="teamPaceImageUrl"
          :src="teamPaceImageUrl"
          class="hud-img"
          :style="{ width: hudSize + 'px', height: hudSize + 'px' }"
          alt=""
        />
        <div v-if="teamPaceText" class="hud-text">{{ teamPaceText }}</div>
      </div>

      <!-- Path -->
      <PathSVG
        v-if="pathNodes.length"
        :nodes="pathNodes"
        :design="{ w: designW, h: designH }"
        :stroke="brand.highlight"
      />

      <BoardBadgesSVG
        v-if="pathNodes.length"
        :nodes="nodes"
        :design="{ w: designW, h: designH }"
        :points-per-step="pointsPerStep"
        :every="badgeEveryPoints"
        :look-ahead="badgeLookAhead"
      />
      <!-- Milestones & quizzes -->
      <BoardIcon
        v-for="el in icons"
        :key="el.key"
        :x="nodeX(el.index)"
        :y="nodeY(el.index)"
        :design="{ w: designW, h: designH }"
        :src="el.iconUrl"
        :size="iconSize"
        :locked="elLocked(el)"
        :title="elTitle(el)"
        :style="elStyle(el)"
        @click.native="onIconClick(el)"
      />


      <!-- Team avatars -->
      <TeamPiece
        v-for="t in teams"
        :key="t.id"
        :team="t"
        :x="nodeX(t.step || 0)"
        :y="nodeY(t.step || 0)"
        :design="{ w: designW, h: designH }"
        :size="avatarSize"
        :myTeam="!!t.myTeam"
      />

      <!-- Status strip (tabs + text) -->
      <div v-if="statusText" class="status-strip">
        <div class="status-tabs">
          <button
            class="status-tab"
            :class="{ active: statusMode === 'next' }"
            @click="statusMode = 'next'"
            type="button"
          >
            {{ tShort('dashboard.next_stop', 'Next') }}
          </button>
          <button
            class="status-tab"
            :class="{ active: statusMode === 'finish' }"
            @click="statusMode = 'finish'"
            type="button"
          >
            {{ tShort('dashboard.finish_line', 'Finish') }}
          </button>
        </div>
        <div class="status-text">{{ statusText }}</div>
      </div>


      <div v-if="!loading && !pathNodes.length" class="empty-state">
        <div class="empty-title">{{ emptyTitle }}</div>
        <div class="empty-sub">{{ emptyHint }}</div>
      </div>

      <QuizModalV2
        ref="quizModal"
        :quiz_id="activeQuizId"
        @answered="onQuizAnswered"
        @close-modal="onQuizClosed"
      />


      <MilestoneModal
        :milestone="activeMilestone"
        @close="onMilestoneClosed"
      />      
    </div> <!-- closes .board-wrap -->
   </div>  <!-- closes .board-shell -->
  </b-overlay>
</template>

<script>
import PathSVG from "./PathSVG.vue"
import BoardIcon from "./BoardIcon.vue"
import TeamPiece from "./TeamPiece.vue"
import BoardBadgesSVG from "./BoardBadgesSVG.vue"
import MilestoneModal from "./MilestoneModal.vue"
import CsrStrip from "./CsrStrip.vue"
import { fetchGameboard, fetchTeamPace } from "@/services/gameboard.service"
import { toPathPoints } from "@/utils/gameboard-math"
import QuizModalV2 from "./QuizModalV2.vue"
import { fetchCSR } from "@/services/csr.service"

const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1

const BRAND = {
  primary:   "#E63375",
  secondary: "#00C782",
  dark:      "#1a1947",
  highlight: "#ffffff"
}

function rafThrottle(fn){
  let id = null
  return function(...args){
    if (id) return
    id = requestAnimationFrame(() => { id = null; fn.apply(this, args) })
  }
}

export default {
  name: "GameBoard",
  components: { PathSVG, BoardIcon, TeamPiece, QuizModalV2, MilestoneModal, BoardBadgesSVG, CsrStrip },
  data(){
    return {
      loading: false,
      csr: null,
      designW: 1080,
      designH: 1920,
      bgUrl: "",
      // REACTIVE state that templates depend on:
      nodes: [],        // full node objects
      pathNodes: [],    // simplified nodes for the path
      icons: [],        // [{ index, iconUrl, raw }]
      teams: [],        // [{ id, name, iconUrl, myTeam, step }]
      // sizing
      wrapHeight: 0,
      wrapWidth: 0,
      avatarSize: 48,
      iconSize: 36,
      stepDuration: 850,
      ro: null,
      // quiz state
      activeQuizId: null,
      quizResolver: null,
      answeredOverride: Object.create(null), // UI-only: mark answered instantly in this session
      autoOpenedQuiz: Object.create(null), 

     // milestone state
     activeMilestone: null,
     milestoneResolver: null,    
     
     // Team Pace (HUD)
     teamPaceText: '',
     teamPaceImageUrl: '',
     hudSize: 48,      // scales with board width
     hudMargin: 12,
     
     pointsPerStep: 100,
     endIndex: 0,
     statusMode: 'next',        // 'next' | 'finish'
     statusNextText: '',
     statusFinishText: '', 
     
     badgeEveryPoints: 800,
     badgeLookAhead: 2,    
     dpr: DPR, 
    }
  },
  computed:{
    brand(){ return BRAND },
    myStep() {
      const list = this.teams || []
      const mine = list.find(t => t && t.myTeam)
      const step = mine && typeof mine.step === 'number' ? mine.step : 0
      return step
    },
    bgSrc(){
      return this.bgUrl
    },
    wrapStyle(){
      const maxCss = Math.floor(this.designW / (this.dpr || 1)) // e.g. 1080/2 = 540px on DPR=2
      return {
        '--gb-ar': `${this.designW} / ${this.designH}`,
        height: `${this.wrapHeight}px`,
        maxWidth: `${maxCss}px`,            // ← never upscale: stays crisp
        margin: '0 auto'                    // ← center the board within its column
      }
    },
    // i18n fallbacks
    emptyTitle(){
      try {
        const v = this.$t('dashboard.no_gameboard_available')
        return (typeof v === 'string' && !/^dashboard\./.test(v)) ? v : 'No active gameboard'
      } catch (e) { return 'No active gameboard' }
    },
    emptyHint(){
      try {
        const v = this.$t('dashboard.no_gameboard_hint')
        return (typeof v === 'string' && !/^dashboard\./.test(v)) ? v :
          'Please check that a campaign is active and the gameboard nodes are configured.'
      } catch (e) {
        return 'Please check that a campaign is active and the gameboard nodes are configured.'
      }
    },
    myArrivedStep() {
      const list = this.teams || []
      const mine = list.find(t => t && t.myTeam)
      const step = mine && typeof mine.arrived === 'number' ? mine.arrived : 0
      return step
    },    
    statusText(){
      return this.statusMode === 'finish' ? this.statusFinishText : this.statusNextText
    },    
  },
  methods:{
    async load(){
      this.loading = true

      // reset per load so tests / reloads always allow auto-open again
      this.autoOpenedQuiz = Object.create(null)

      try {
        // 1) Fetch
        const payload   = await fetchGameboard()
        const gameBoard = (payload && payload.gameBoard) || {}
        const teamList  = (payload && payload.teams) || []

        // 2) Board sizing + background
        this.designW = Number(gameBoard.width)  || 1080
        this.designH = Number(gameBoard.height) || 1920
        this.bgUrl   = (gameBoard.background && gameBoard.background.url) || ""

        // 3) Nodes + path (design px)
        this.nodes     = Array.isArray(gameBoard.nodes) ? gameBoard.nodes : []
        this.pathNodes = toPathPoints(this.nodes)

        // points and END node
        this.pointsPerStep = Number(gameBoard.pointsPerStep) || 100
        const end = this.nodes.find(n => n && n.type === 'END')
        this.endIndex = end ? end.index : Math.max(0, this.nodes.length - 1)        

        // 4) Icons (milestones / quizzes)
        const icons = []
        for (let i = 0; i < this.nodes.length; i++) {
          const els = (this.nodes[i] && this.nodes[i].elements) || []
          for (let j = 0; j < els.length; j++) {
            const el = els[j]
            if (!el || !el.iconUrl) continue

            const isQuiz = (el.element_type === 'quiz' || el.type === 'quiz') && el.quizId
            const isMilestone = (el.element_type === 'milestone') || (el.type === 'milestone') || !!el.milestone

            if (!isQuiz && !isMilestone) continue

            icons.push({
              key: isQuiz ? `quiz-${el.quizId}` : `ms-${(el.milestone && el.milestone.id) || i}-${j}`,
              index: i,
              iconUrl: el.iconUrl,
              raw: el,
              kind: isQuiz ? 'quiz' : 'milestone',
              quizId: isQuiz ? el.quizId : null,
              backendAnswered: isQuiz ? !!el.answered : false
            })
          }
        }
        this.icons = icons


        // 5) Teams → build animation path from completedSteps
        const mappedTeams = []
        for (let k = 0; k < teamList.length; k++) {
          const t = teamList[k] || {}
          let path = Array.isArray(t.completedSteps) ? t.completedSteps.slice() : []
          path = path
            .filter(s => typeof s === 'number' && isFinite(s))
            .sort((a, b) => a - b)

          const start  = path.length ? path[0] : 0
          const target = path.length ? path[path.length - 1] : 0

          mappedTeams.push({
            id: t.id,
            name: t.name,
            iconUrl: t.iconUrl,
            myTeam: !!t.myTeam,
            step: start,       // visual position (moves immediately)
            arrived: start,    // "unlock" position (updates after hop finishes)
            target,
            _path: path
          })
        }
        this.teams = mappedTeams

      try {
        const pace = await fetchTeamPace()
        this.teamPaceText = pace.text || ''
        this.teamPaceImageUrl = pace.imageUrl || ''
      } catch (e) {
        // optional: silent if endpoint empty
        // console.warn('[GameBoard] team pace fetch failed', e)
      }

      // Load the CSR Module (if applicable)
      await this.loadCsr()

      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[GameBoard] load failed:', e)
      } finally {
        this.loading = false
        this.onResize()

        this.updateStatus()
        // 6) Animate ALL teams after first paint
        this.$nextTick(() => this.animateAllTeams())
      }
    },

    sleep(ms) {
      return new Promise(r => setTimeout(r, ms))
    },

    async animateAllTeams() {
      const list = this.teams || []
      // animate all teams concurrently (simple and performant)
      await Promise.all(list.map(t => this.animateTeam(t)))
    },

    async animateTeam(team) {
      const path = Array.isArray(team._path) ? team._path : []
      if (!path.length) return

      // Start pose
      team.step = path[0]
      team.arrived = path[0]
      await this.$nextTick()

      for (let i = 1; i < path.length; i++) {
        const next = path[i]
        if (next <= team.step) continue

        // 1) start the hop (position updates now)
        team.step = next
        await this.$nextTick()

        // 2) wait until the hop visually finishes
        await this.sleep(this.stepDuration + 50)

        // 3) mark arrival → flags unlock now
        const prevArrived = team.arrived
        team.arrived = next

        this.updateStatus()
      
        // 4) handle quiz/milestone AFTER arrival for MY team
        if (team.myTeam) {

          // auto-open quiz if we passed it during this hop
          await this.maybeAutoOpenQuiz(prevArrived, next)

          const els = (this.nodes[next] && this.nodes[next].elements) || []

          // milestone next
          const iconEl = els.find(e => e && e.iconUrl)
          const dataEl = els.find(e => e && e.milestone)
          if (dataEl) {
            const payload = {
              id: dataEl.milestone && dataEl.milestone.id,
              name: (dataEl.milestone && dataEl.milestone.name) || 'Milestone',
              message: (dataEl.milestone && dataEl.milestone.message) || '',
              imageUrl: iconEl ? iconEl.iconUrl : ''
            }
            await this.openMilestone(payload)
          }
        }
      }
    },
    async maybeAutoOpenQuiz(prevArrived, nextArrived){
      // only auto-open when we actually advanced
      if (!(nextArrived > prevArrived)) return

      for (let idx = prevArrived + 1; idx <= nextArrived; idx++){
        const els = (this.nodes[idx] && this.nodes[idx].elements) || []
        const q = els.find(e => (e.element_type === 'quiz' || e.type === 'quiz') && e.quizId)
        if (!q) continue

        const quizId = q.quizId
        const alreadyAnswered = !!q.answered || !!this.answeredOverride[quizId]
        if (alreadyAnswered) continue

        if (this.autoOpenedQuiz[quizId]) continue

        this.autoOpenedQuiz[quizId] = true
        await this.openQuiz(quizId)
        break // only open one quiz per hop
      }
    },
    isQuizIcon(el){
      return el && el.kind === 'quiz' && el.quizId
    },
    quizPassed(el){
      if (!this.isQuizIcon(el)) return false
      const my = (this.teams || []).find(t => t && t.myTeam)
      const arrived = my && typeof my.arrived === 'number' ? my.arrived : 0
      return arrived >= el.index
    },
    quizAnswered(el){
      if (!this.isQuizIcon(el)) return false
      return !!el.backendAnswered || !!this.answeredOverride[el.quizId]
    },
    quizTappable(el){
      return this.isQuizIcon(el) && this.quizPassed(el) && !this.quizAnswered(el)
    },
    elLocked(el){
      // milestone rules unchanged
      if (this.isMilestone(el)) return !this.isMilestonePassed(el.index)

      // quiz is locked when ahead of avatar (not passed)
      if (this.isQuizIcon(el)) return !this.quizPassed(el)

      return false
    },
    elStyle(el){
      // Applies opacity per spec
      if (this.isQuizIcon(el)){
        if (!this.quizPassed(el)) return { opacity: 0.7, cursor: 'default', pointerEvents: 'none' }
        if (this.quizAnswered(el)) return { opacity: 0.65, cursor: 'default', pointerEvents: 'none' }
        return { opacity: 1.0, cursor: 'pointer', pointerEvents: 'auto' }
      }

      // milestones: leave as-is (or you can add similar dimming if you want)
      return {}
    },
    onIconClick(el){
      if (!this.quizTappable(el)) return
      this.openQuiz(el.quizId)
    },
    onQuizAnswered(payload){
      // payload expected: { quizId }
      const id = payload && payload.quizId
      if (!id) return

      // instant UI update this session
      this.$set(this.answeredOverride, id, true)

      // also prevents reopening while still on the board
      // (backend will handle cross-session)
    },
  
    // SAFE accessors (avoid ?. in templates)
    nodeX(i){
      const a = this.nodes
      return a && a[i] && typeof a[i].x === 'number' ? a[i].x : 0
    },
    nodeY(i){
      const a = this.nodes
      return a && a[i] && typeof a[i].y === 'number' ? a[i].y : 0
    },

    isMilestone(el){
      const r = el && el.raw ? el.raw : {}
      return (r.element_type === 'milestone') || (r.type === 'milestone') || !!r.milestone
    },
    isMilestonePassed(idx){
      const my = (this.teams || []).find(t => t && t.myTeam)
      const arrived = my && typeof my.arrived === 'number' ? my.arrived : 0
      const n = Number(idx)
      return arrived >= (isFinite(n) ? n : 0)
    },
    elTitle(el){
      const r = el && el.raw ? el.raw : {}
      if (r.milestone && r.milestone.name) return r.milestone.name
      if (r.element_type === 'quiz' || r.type === 'quiz') return 'Quiz'
      return ''
    },

    onResize: rafThrottle(function(){
      const el = this.$refs && this.$refs.wrap
      if (!el) return
      const w = el.clientWidth || 0
      this.wrapWidth  = w
      this.wrapHeight = Math.round(w * (this.designH / this.designW))
      // sizes scale with width; tweak ratios to taste
      this.avatarSize = Math.max(37, Math.round(w * 0.045)) // ~4.5% of width
      this.iconSize   = Math.max(36, Math.round(w * 0.035)) // ~3.5% of width
      this.hudSize    = Math.max(32, Math.round(w * 0.12))  // ~12% of width
      this.hudMargin  = Math.max(8,  Math.round(w * 0.03))  // ~3% of width
    }),

    openQuiz(quizId) {
      // returns a Promise that resolves when the modal closes
      return new Promise((resolve) => {
        this.quizResolver = resolve
        this.activeQuizId = quizId // QuizModal watches quiz_id and opens itself
      })
    },
    onQuizResult(payload){
      this.lastQuizResult = payload || null
    },    
    onQuizClosed() {
      this.activeQuizId = null
      const r = this.quizResolver
      this.quizResolver = null
      if (typeof r === 'function') r()
    },
    openMilestone(payload){
      // payload: { id, name, message, imageUrl, rewardText? }
      return new Promise((resolve) => {
        this.milestoneResolver = resolve
        this.activeMilestone = payload
      })
    },
    onMilestoneClosed(){
      this.activeMilestone = null
      const r = this.milestoneResolver
      this.milestoneResolver = null
      if (typeof r === 'function') r()
    },

    // tiny i18n helper with fallback
    tShort(key, fallback){
      try {
        const v = this.$t ? this.$t(key) : ''
        return (typeof v === 'string' && !/^dashboard\./.test(v)) ? v : fallback
      } catch { return fallback }
    },

    formatPts(n){
      const val = Math.max(0, Math.round(n || 0))
      const unit = this.tShort('points', 'pts')
      return `${val}\u202F${unit}`
    },

    updateStatus(){
      // my arrived step (what user *has* reached)
      const my = (this.teams || []).find(t => t && t.myTeam)
      const arrived = my && typeof my.arrived === 'number' ? my.arrived : 0

      // next milestone ahead
      let next = null
      for (let i = arrived + 1; i < this.nodes.length; i++){
        const els = (this.nodes[i] && this.nodes[i].elements) || []
        if (els.some(e => e && e.milestone)) { next = i; break }
      }

      if (next != null){
        const pts = (next - arrived) * this.pointsPerStep
        const name = (() => {
          const els = (this.nodes[next] && this.nodes[next].elements) || []
          const ms = els.find(e => e && e.milestone)
          return (ms && ms.milestone && ms.milestone.name) || this.$t("dashboard.next_stop")
        })()
        this.statusNextText = `${this.$t('dashboard.next_stop')}: ${name} · ${this.formatPts(pts)}`
      } else {
        // no next milestone ahead
        const pts = Math.max(0, this.endIndex - arrived) * this.pointsPerStep
        this.statusNextText = pts
          ? `${this.$t('dashboard.finish_line')} · ${this.formatPts(pts)}`
          : ''
      }

      // finish text
      const ptsToFinish = Math.max(0, this.endIndex - arrived) * this.pointsPerStep
      this.statusFinishText = ptsToFinish
        ? `${this.tShort('dashboard.finish_line', 'Finish line')} · ${this.formatPts(ptsToFinish)}`
        : ''
    },

    async loadCsr() {
      try {
        this.csr = await fetchCSR() // null if 204
      } catch (e) {
        this.csr = null
      }
    },
  },
  mounted(){
    this.$nextTick(async () => {
      await this.load()
      window.addEventListener('resize', this.onResize, { passive: true })
      if (window.ResizeObserver){
        this.ro = new ResizeObserver(this.onResize)
        this.ro.observe(this.$refs.wrap)
      }
    })
  },
  beforeDestroy(){
    window.removeEventListener('resize', this.onResize)
    if (this.ro) this.ro.disconnect()
  }
}
</script>

<style scoped>
.board-wrap{
  position: relative;
  width: 100%;
  aspect-ratio: var(--gb-ar); /* modern browsers */
  overflow: visible;
  background-color: #B0BFBF;
}
.board-bg{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;   /* board art already matches aspect */
  border-radius: 15px;
  z-index: 1;
}
.empty-state{
  position:absolute; inset:0;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  text-align:center; background: rgba(255,255,255,0.6);
  border-radius: 15px;
}
.empty-title { color:#1a1947; font-weight:700; margin-bottom:6px; }
.empty-sub   { color:#545454; font-size:14px; }

.hud{
  position: absolute;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0,0,0,0.40);
  padding: 6px 10px;
  border-radius: 8px;
}
.hud-img{ display: block; object-fit: contain; }
.hud-text{
  color: #fff;
  font-size: 13px;
  line-height: 1.2;
}

.status-strip{
  position: absolute;
  left: 8px; right: 8px; bottom: 8px;
  display: flex; align-items: center; gap: 10px;
  background: rgba(0,0,0,0.40);
  padding: 6px 10px;
  border-radius: 10px;
  z-index: 6;
}
.status-tabs{
  display: inline-flex; overflow: hidden; border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.25);
}
.status-tab{
  appearance: none; border: 0; cursor: pointer;
  background: transparent; color: #fff;
  padding: 6px 10px; font-size: 12px; line-height: 1;
}
.status-tab.active{
  background: #D42C68; /* brand primary */
  color: #fff;
}
.status-text{
  color: #fff; font-size: 12px; line-height: 1.2;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  flex: 1 1 auto;
}

.board-shell{ width: 100%; }

.board-wrap.has-csr .board-bg{
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}


</style>
